import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem } from '@mui/material';

import { PATH_AUTH, PATH_DASHBOARD } from 'routes/paths';

import { USER_ROLES } from 'helpers/constants/user';
import useAuth from 'hooks/useAuth';
import useIsMountedRef from 'hooks/useIsMountedRef';

import Iconify from 'components/Iconify';
import MenuPopover from 'components/MenuPopover';
import MyAvatar from 'components/MyAvatar';
import { IconButtonAnimate } from 'components/animate';

export default function AccountPopover() {
  const navigate = useNavigate();
  const isMountedRef = useIsMountedRef();

  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const isManagingAdmin = +user?.role === USER_ROLES.MANAGING_ADMIN;

  const menuOptions = [
    { label: 'Պրոֆիլ', linkTo: PATH_DASHBOARD.user.root, icon: 'eva:person-fill' },
    ...(isManagingAdmin
      ? [{ label: 'Կարգավորումներ', linkTo: PATH_DASHBOARD.user.accountSettings, icon: 'ic:round-settings' }]
      : []),
  ];

  const [open, setOpen] = useState(null);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH_AUTH.login, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      enqueueSnackbar('Համակարգից դուրս գալ չի հաջողվել', { variant: 'error' });
    }
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <MyAvatar />
      </IconButtonAnimate>

      <MenuPopover
        open={!!open}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {`${user.first_name} ${user.last_name}`}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {menuOptions.map((option) => (
            <MenuItem key={option.label} to={option.linkTo} component={RouterLink} onClick={handleClose}>
              <Iconify icon={option.icon} sx={{ mr: 1, width: 20, height: 20, flexShrink: 0 }} />
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          <Iconify icon="eva:log-out-fill" sx={{ mr: 1, width: 20, height: 20, flexShrink: 0 }} />
          Դուրս գալ
        </MenuItem>
      </MenuPopover>
    </>
  );
}
